import React, { ReactElement, useEffect, useRef, useState } from 'react';
import {
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Spin,
    Typography,
    Upload,
    message,
} from 'antd';
import * as adminApi from 'js/utils/adminApi';
import { IEvent } from 'store/slices/types';

const { Title } = Typography;
const { TextArea, Password } = Input;

export default function SettingsPage(): ReactElement {
    const [events, setEvents] = useState<IEvent[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingEvent, setSavingEvent] = useState(false);
    const [savingVK, setSavingVK] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [assets, setAssets] = useState<adminApi.IEventAsset[]>([]);
    const [uploadingAsset, setUploadingAsset] = useState<string | null>(null);
    const assetEventRef = useRef<number | null>(null);
    const [eventForm] = Form.useForm();
    const [vkForm] = Form.useForm();
    const [createForm] = Form.useForm();

    useEffect(() => {
        adminApi
            .listEvents()
            .then(list => {
                setEvents(list);
                if (list.length) setEventId(list[0].id);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (eventId === null) {
            setAssets([]);
            assetEventRef.current = null;
            return;
        }
        if (assetEventRef.current === eventId) return;
        assetEventRef.current = eventId;
        adminApi
            .listEventAssets(eventId)
            .then(setAssets)
            .catch(() => setAssets([]));
    }, [eventId]);

    useEffect(() => {
        const current = events.find(e => e.id === eventId);
        if (!current) return;
        /* eslint-disable camelcase */
        eventForm.setFieldsValue({
            name: current.name,
            on_start_message: current.on_start_message,
            on_not_found_message: current.on_not_found_message,
            on_scan_message: current.on_scan_message,
        });
        vkForm.setFieldsValue({
            name: current.vk_group?.name,
            vk_id: current.vk_group?.vk_id,
            token: '',
            confirmation_code: current.vk_group?.confirmation_code,
        });
        /* eslint-enable camelcase */
    }, [eventId, events, eventForm, vkForm]);

    if (loading) return <Spin />;
    const current = events.find(e => e.id === eventId);

    const saveEvent = async (): Promise<void> => {
        if (!current) return;
        const values = await eventForm.validateFields();
        setSavingEvent(true);
        try {
            const updated = await adminApi.updateEvent(current.id, values);
            setEvents(prev =>
                prev.map(e => (e.id === updated.id ? { ...e, ...updated } : e))
            );
            message.success('Event сохранён');
        } finally {
            setSavingEvent(false);
        }
    };

    const saveVK = async (): Promise<void> => {
        if (!current) return;
        const values = await vkForm.validateFields();
        /* eslint-disable camelcase */
        const payload = {
            name: values.name,
            vk_id: values.vk_id,
            confirmation_code: values.confirmation_code,
            ...(values.token ? { token: values.token } : {}),
        };
        /* eslint-enable camelcase */
        setSavingVK(true);
        try {
            const updated = await adminApi.patchVKGroup(current.id, payload);
            setEvents(prev =>
                prev.map(e =>
                    // eslint-disable-next-line camelcase
                    e.id === current.id ? { ...e, vk_group: updated } : e
                )
            );
            vkForm.setFieldValue('token', '');
            message.success('VK Group сохранён');
        } finally {
            setSavingVK(false);
        }
    };

    const uploadAsset = async (
        kind: 'map' | 'works_order',
        file: File
    ): Promise<boolean> => {
        if (!current) return false;
        setUploadingAsset(kind);
        try {
            const created = await adminApi.uploadEventAsset(
                current.id,
                kind,
                file
            );
            setAssets(prev => {
                const without = prev.filter(a => a.kind !== kind);
                return [...without, created];
            });
            message.success('Файл загружен');
        } catch {
            message.error('Ошибка загрузки');
        } finally {
            setUploadingAsset(null);
        }
        return false;
    };

    const onCreate = async (): Promise<void> => {
        const values = await createForm.validateFields();
        setCreating(true);
        try {
            /* eslint-disable camelcase */
            const created = await adminApi.createEvent({
                name: values.name,
                vk_group: {
                    name: values.vk_name,
                    vk_id: values.vk_id,
                    token: values.token,
                    confirmation_code: values.confirmation_code,
                },
            });
            /* eslint-enable camelcase */
            setEvents(prev => [...prev, created]);
            setEventId(created.id);
            createForm.resetFields();
            setCreateOpen(false);
            message.success('Event создан');
        } finally {
            setCreating(false);
        }
    };

    return (
        <div>
            <Title level={3}>Настройки проекта</Title>
            <Space style={{ marginBottom: 24 }}>
                <span>Event:</span>
                <Select
                    style={{ width: 280 }}
                    value={eventId ?? undefined}
                    onChange={(v): void => setEventId(v)}
                    options={events.map(e => ({ value: e.id, label: e.name }))}
                />
                <Button onClick={(): void => setCreateOpen(true)}>
                    Создать event
                </Button>
            </Space>

            <Modal
                open={createOpen}
                title="Новый event"
                onCancel={(): void => setCreateOpen(false)}
                onOk={onCreate}
                okButtonProps={{ loading: creating }}
                okText="Создать"
                cancelText="Отмена"
                destroyOnHidden
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    preserve={false}
                    autoComplete="off"
                >
                    <Form.Item
                        name="name"
                        label="Название event"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Lis Vegas 2026" />
                    </Form.Item>
                    <Form.Item
                        name="vk_name"
                        label="Имя VK-группы"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Lis Vegas" />
                    </Form.Item>
                    <Form.Item
                        name="vk_id"
                        label="ID VK-группы"
                        rules={[{ required: true }]}
                    >
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="token"
                        label="VK API token"
                        rules={[{ required: true }]}
                    >
                        <Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item
                        name="confirmation_code"
                        label="Confirmation code"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            {current && (
                <Space align="start" size={24} style={{ display: 'flex' }}>
                    <Card title="Event" style={{ width: 480 }}>
                        <Form form={eventForm} layout="vertical">
                            <Form.Item name="name" label="Название">
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="on_start_message"
                                label="Приветствие"
                            >
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item
                                name="on_not_found_message"
                                label="Сообщение «не найден»"
                            >
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item
                                name="on_scan_message"
                                label="Сообщение при сканировании"
                            >
                                <TextArea rows={3} />
                            </Form.Item>
                            <Button
                                type="primary"
                                loading={savingEvent}
                                onClick={saveEvent}
                            >
                                Сохранить
                            </Button>
                        </Form>
                    </Card>

                    <Card title="Чат-бот: материалы" style={{ width: 480 }}>
                        {(['map', 'works_order'] as const).map(kind => {
                            const asset = assets.find(a => a.kind === kind);
                            const label =
                                kind === 'map' ? 'Карта' : 'Порядок работ';
                            return (
                                <div key={kind} style={{ marginBottom: 16 }}>
                                    <div>
                                        <b>{label}</b>:{' '}
                                        {asset
                                            ? asset.filename
                                            : 'не загружено'}
                                    </div>
                                    <Upload
                                        accept="image/*"
                                        showUploadList={false}
                                        beforeUpload={(file): boolean => {
                                            uploadAsset(kind, file).catch(
                                                () => {
                                                    /* handled inside */
                                                }
                                            );
                                            return false;
                                        }}
                                    >
                                        <Button
                                            loading={uploadingAsset === kind}
                                        >
                                            Загрузить
                                        </Button>
                                    </Upload>
                                </div>
                            );
                        })}
                    </Card>

                    <Card title="VK Group" style={{ width: 480 }}>
                        <Form form={vkForm} layout="vertical">
                            <Form.Item name="name" label="Имя">
                                <Input />
                            </Form.Item>
                            <Form.Item name="vk_id" label="VK Group ID">
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="token"
                                label="Новый токен (оставь пустым чтобы не менять)"
                            >
                                <Password
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                />
                            </Form.Item>
                            <Form.Item
                                name="confirmation_code"
                                label="Confirmation code"
                            >
                                <Input />
                            </Form.Item>
                            <Button
                                type="primary"
                                loading={savingVK}
                                onClick={saveVK}
                            >
                                Сохранить
                            </Button>
                        </Form>
                    </Card>
                </Space>
            )}
        </div>
    );
}
